import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../../../../../../assets/icons/close.svg";
import { ReactComponent as LogoutIcon } from "../../../../../../../assets/icons/logout.svg";
import { ReactComponent as EditIcon } from "../../../../../../../assets/icons/edit.svg";
import { AccountItem } from "../../../../../../../components/common/AccountItem";
import { RootState } from "../../../../../../../store";
import { selectUserInfo } from "../../../../../../../store/selectors";
import { connect } from "react-redux";
import { useCookies } from "react-cookie";
import { UserActionCreator } from "../../../../../../../store/userActions";
import { PaidPeriod } from "../PaidPeriod";
import { TestPeriod } from "../TestPeriod";
import { NavBar } from "../NavBar";
import { broadcastAuthLogout } from "../../../../../../../utils/authSession";

type Props = {
    user: any;
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
    logout: () => void;
};

const _SideMenu = ({ user, isOpen, setIsOpen, logout }: Props) => {
    const [, , removeCookie] = useCookies();
    const isAuth = Boolean(user);

    useEffect(() => {
        if (!isAuth && isOpen) {
            setIsOpen(false);
        }
    }, [isAuth, isOpen, setIsOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    if (!isAuth) return null;

    const paidTillDate = user?.paidTill
        ? new Date(user.paidTill).toLocaleString("ru-RU", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
        : undefined;

    const testPeriodTillDate = user?.testPeriodTill
        ? new Date(user.testPeriodTill).toLocaleString("ru-RU", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
        : undefined;

    const shouldRenderPaid = !user.testPeriod || user.paid;
    const shouldRenderTest = user.testPeriod && !user.paid;

    const handleClose = () => setIsOpen(false);

    const handleLogout = () => {
        removeCookie("userToken", { path: "/" });
        removeCookie("rememberMe", { path: "/" });
        broadcastAuthLogout();
        setIsOpen(false);
        logout();
    };

    return (
        <>
            {/* overlay */}
            <div
                className={`fixed inset-0 z-[501] bg-black/15 backdrop-blur-md transition-opacity ${
                    isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
                onClick={handleClose}
            />

            {/* drawer */}
            <div
                className={`
          fixed inset-y-0 right-0 z-[502] w-full bg-white transition-transform duration-300
          px-6 py-7 text-[16px]
          ${isOpen ? "translate-x-0" : "translate-x-full"}
          lg:w-[430px]
          shadow-none lg:shadow-[0_0_18px_0_rgba(0,0,0,0.15)]
          flex flex-col
        `}
            >
                {/* close */}
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label="Закрыть меню"
                    className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 transition"
                >
                    <CloseIcon stroke="#818c99" width="18" height="18"/>
                </button>

                {/* scrollable content */}
                <div className="flex-1 flex flex-col gap-5 mt-4 overflow-y-auto pb-6">
                    {/* account card */}
                    <div className="rounded-3xl bg-white shadow-sm px-5 py-4 border border-[#f0e4ee]">
                        <AccountItem account={{ user }} />

                        <div className="mt-3">
                            <Link
                                to="/personal-data"
                                onClick={handleClose}
                                className="inline-flex items-center gap-2 rounded-full bg-[#f5e0ee] px-3 py-[6px] text-xs font-medium text-[#c06198] hover:bg-[#f0c8e4] transition"
                                aria-label="Редактировать профиль"
                                title="Редактировать профиль"
                            >
                                <EditIcon width={16} height={16} />
                                <span>Редактировать профиль</span>
                            </Link>
                        </div>
                    </div>

                    {shouldRenderPaid && (
                        <div className="rounded-3xl bg-white shadow-sm px-5 py-4 border border-[#f0e4ee]">
                            <PaidPeriod
                                paidTill={paidTillDate}
                                handleClose={handleClose}
                            />
                        </div>
                    )}

                    {shouldRenderTest && (
                        <div className="rounded-3xl bg-white shadow-sm px-5 py-4 border border-[#f0e4ee]">
                            <TestPeriod
                                testPeriod={user.testPeriod}
                                testPeriodTill={testPeriodTillDate}
                                handleClose={handleClose}
                            />
                        </div>
                    )}

                    {/* nav card */}
                    <div className="rounded-3xl bg-white shadow-sm px-4 py-3 border border-[#f0e4ee]">
                        <NavBar onNavigate={() => setIsOpen(false)} />
                    </div>
                </div>

                {/* logout */}
                <button
                    className="mt-2 inline-flex items-center gap-2 text-[#d11a1a] text-[15px] font-medium hover:opacity-90 transition"
                    onClick={handleLogout}
                >
          <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-white shadow-sm border border-[#ffd3d3]">
            <LogoutIcon stroke="#f00000" width="16" height="16" />
          </span>
                    Выйти
                </button>
            </div>
        </>
    );
};



const mapStateToProps = (state: RootState) => ({
    user: selectUserInfo(state),
});

const mapDispatchToProps = {
    logout: UserActionCreator.logout,
};

export const SideMenu = connect(mapStateToProps, mapDispatchToProps)(_SideMenu);
